import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Bot,
  FolderOpen,
  Users,
  BookOpen,
  Tag,
  FileText,
  Image,
  Video,
  Contact,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Folder,
  Loader,
  AlertCircle,
  FileUp,
  Download,
} from "lucide-react";
import { BarChart3, FlaskConical, Globe } from "../utils/icons";
import { Calendar } from "lucide-react";
import { supabase, enhancedQuery } from "../lib/supabase";
import SEO from "./SEO";
import { useAuthStore } from "../store/authStore";

interface NavGroupProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

interface FolderInfo {
  name: string;
  count: number;
}

interface SidebarItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  subItems?: FolderInfo[];
}

const NavGroup: React.FC<NavGroupProps> = ({
  title,
  isExpanded,
  onToggle,
  children,
}) => (
  <div>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-2 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-200 hover:text-gray-900"
    >
      <span>{title}</span>
      {isExpanded ? (
        <ChevronUp className="h-4 w-4" />
      ) : (
        <ChevronDown className="h-4 w-4" />
      )}
    </button>
    {isExpanded && <div className="space-y-1 pl-3">{children}</div>}
  </div>
);

const SidebarItem: React.FC<SidebarItemProps> = ({
  to,
  icon,
  label,
  isActive,
  subItems,
}) => (
  <div>
    <Link
      to={to}
      className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
        isActive
          ? "bg-gray-200 text-gray-900"
          : "text-gray-700 hover:bg-gray-200 hover:text-gray-900"
      }`}
    >
      {icon}
      <span className="ml-3">{label}</span>
    </Link>
    {subItems && subItems.length > 0 && (
      <div className="ml-8 mt-1 space-y-1">
        {subItems.map((item) => (
          <Link
            key={item.name}
            to={`${to}?folder=${item.name}`}
            className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <Folder className="h-4 w-4 mr-2" />
            <span>{item.name}</span>
            <span className="ml-auto text-xs text-gray-500">
              ({item.count})
            </span>
          </Link>
        ))}
      </div>
    )}
  </div>
);

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading,
    error: authError,
    checkAuth,
  } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {
      CRM: true,
      "Content Management": false,
      "Course Management": false,
      "Media Management": false,
    },
  );
  const [assetFolders, setAssetFolders] = useState<FolderInfo[]>([]);
  const [videoFolders, setVideoFolders] = useState<FolderInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/admin/login");
      return;
    }

    const fetchFolders = async () => {
      if (!user || !isMountedRef.current) return;

      try {
        if (isMountedRef.current) {
          setLoading(true);
          setError(null);
        }

        // Fetch asset folders
        const { data: assetData, error: assetError } = await enhancedQuery(
          async (signal) => {
            // Get all assets from database to count by folder
            const { data: dbAssets, error: dbError } = await supabase
              .from("assets")
              .select("folder")
              .abortSignal(signal);

            if (dbError) throw dbError;

            // Count assets by folder
            const folderCounts = (dbAssets || []).reduce(
              (acc: Record<string, number>, asset) => {
                if (asset.folder) {
                  acc[String(asset.folder)] =
                    (acc[String(asset.folder)] || 0) + 1;
                }
                return acc;
              },
              {},
            );

            // List all folders in storage
            const { data: storageData, error: storageError } =
              await supabase.storage.from("assets").list("", {
                limit: 1000,
                sortBy: { column: "name", order: "asc" },
              });

            if (storageError) throw storageError;

            return (storageData || [])
              .filter((item) => !String(item.name || "").includes("."))
              .map((item) => ({
                name: String(item.name || ""),
                count: folderCounts[String(item.name || "")] || 0,
              }))
              .sort((a, b) => a.name.localeCompare(b.name));
          },
        );

        if (assetError) throw assetError;

        // Fetch video folders
        const { data: videoData, error: videoError } = await enhancedQuery(
          async (signal) => {
            const { data: dbVideos, error: dbError } = await supabase
              .from("videos")
              .select("folder")
              .abortSignal(signal);

            if (dbError) throw dbError;

            const folderCounts = (dbVideos || []).reduce(
              (acc: Record<string, number>, video) => {
                if (video.folder) {
                  acc[String(video.folder)] =
                    (acc[String(video.folder)] || 0) + 1;
                }
                return acc;
              },
              {},
            );

            const { data: storageData, error: storageError } =
              await supabase.storage.from("videos").list("", {
                limit: 1000,
                sortBy: { column: "name", order: "asc" },
              });

            if (storageError) throw storageError;

            return (storageData || [])
              .filter((item) => !String(item.name || "").includes("."))
              .map((item) => ({
                name: String(item.name || ""),
                count: folderCounts[String(item.name || "")] || 0,
              }))
              .sort((a, b) => a.name.localeCompare(b.name));
          },
        );

        if (videoError) throw videoError;

        if (isMountedRef.current) {
          setAssetFolders(assetData || []);
          setVideoFolders(videoData || []);
          setError(null);
        }
      } catch (error) {
        console.error("Error fetching folders:", error);
        if (isMountedRef.current) {
          setError(
            error instanceof Error ? error.message : "Failed to load folders",
          );
          setAssetFolders([]);
          setVideoFolders([]);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    if (user) {
      fetchFolders();
    }
  }, [user, authLoading, navigate]);

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader className="h-8 w-8 text-[#F98B3D] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md max-w-lg">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Authentication Error
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{authError}</p>
                <button
                  onClick={() => navigate("/admin/login")}
                  className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Return to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const renderNavigation = () => (
    <nav className="mt-5 flex-1 px-2 space-y-4">
      <NavGroup
        title="CRM"
        isExpanded={expandedGroups["CRM"]}
        onToggle={() => toggleGroup("CRM")}
      >
        <SidebarItem
          to="/admin/crm/contacts"
          icon={<Contact className="h-6 w-6" />}
          label="Contacts"
          isActive={location.pathname.startsWith("/admin/crm/contacts")}
        />
      </NavGroup>
      <NavGroup
        title="Content Management"
        isExpanded={expandedGroups["Content Management"]}
        onToggle={() => toggleGroup("Content Management")}
      >
        <SidebarItem
          to="/admin/posts"
          icon={<FileText className="h-6 w-6" />}
          label="Blog Posts"
          isActive={location.pathname.startsWith("/admin/posts")}
        />
        <SidebarItem
          to="/admin/authors"
          icon={<Users className="h-6 w-6" />}
          label="Authors"
          isActive={location.pathname.startsWith("/admin/authors")}
        />
        <SidebarItem
          to="/admin/categories"
          icon={<FolderOpen className="h-6 w-6" />}
          label="Categories"
          isActive={location.pathname.startsWith("/admin/categories")}
        />
        <SidebarItem
          to="/admin/tags"
          icon={<Tag className="h-6 w-6" />}
          label="Tags"
          isActive={location.pathname.startsWith("/admin/tags")}
        />
        <SidebarItem
          to="/admin/generate-sitemap"
          icon={<Globe className="h-6 w-6" />}
          label="Generate Sitemap"
          isActive={location.pathname.startsWith("/admin/generate-sitemap")}
        />
      </NavGroup>
      <NavGroup
        title="Course Management"
        isExpanded={expandedGroups["Course Management"]}
        onToggle={() => toggleGroup("Course Management")}
      >
        <SidebarItem
          to="/admin/courses"
          icon={<BookOpen className="h-6 w-6" />}
          label="Courses"
          isActive={location.pathname.startsWith("/admin/courses")}
        />
        <SidebarItem
          to="/admin/course-categories"
          icon={<FolderOpen className="h-6 w-6" />}
          label="Categories"
          isActive={location.pathname.startsWith("/admin/course-categories")}
        />
        <SidebarItem
          to="/admin/course-tags"
          icon={<Tag className="h-6 w-6" />}
          label="Tags"
          isActive={location.pathname.startsWith("/admin/course-tags")}
        />
      </NavGroup>
      <NavGroup
        title="Media Management"
        isExpanded={expandedGroups["Media Management"]}
        onToggle={() => toggleGroup("Media Management")}
      >
        <SidebarItem
          to="/admin/assets"
          icon={<Image className="h-6 w-6" />}
          label="Assets"
          isActive={location.pathname.startsWith("/admin/assets")}
          subItems={assetFolders}
        />
        <SidebarItem
          to="/admin/videos"
          icon={<Video className="h-6 w-6" />}
          label="Videos"
          isActive={location.pathname.startsWith("/admin/videos")}
          subItems={videoFolders}
        />
        <SidebarItem
          to="/admin/uploads"
          icon={<FileUp className="h-6 w-6" />}
          label="Uploads"
          isActive={location.pathname.startsWith("/admin/uploads")}
        />
        <SidebarItem
          to="/admin/prompt-downloads"
          icon={<Download className="h-6 w-6" />}
          label="Prompt Downloads"
          isActive={location.pathname.startsWith("/admin/prompt-downloads")}
        />
        <SidebarItem
          to="/admin/course-registrations"
          icon={<Calendar className="h-6 w-6" />}
          label="Course Registrations"
          isActive={location.pathname.startsWith("/admin/course-registrations")}
        />
        <SidebarItem
          to="/admin/ctas"
          icon={<MessageSquare className="h-6 w-6" />}
          label="CTAs"
          isActive={location.pathname.startsWith("/admin/ctas")}
        />
        <SidebarItem
          to="/admin/lab-exercises"
          icon={<FlaskConical className="h-6 w-6" />}
          label="Lab Exercises"
          isActive={location.pathname.startsWith("/admin/lab-exercises")}
        />
        <SidebarItem
          to="/admin/search-analytics"
          icon={<BarChart3 className="h-6 w-6" />}
          label="Search Analytics"
          isActive={location.pathname.startsWith("/admin/search-analytics")}
        />
      </NavGroup>
    </nav>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Only show admin SEO if not on a blog post page */}
      {!location.pathname.includes("/blog/") && (
        <SEO
          title="One80Labs - Admin Dashboard"
          description="Admin dashboard for managing One80Labs content and resources."
          noindex={true}
        />
      )}
      {/* Mobile sidebar */}
      <div className="lg:hidden">
        <div className="fixed inset-0 flex z-40">
          <div
            className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity ease-linear duration-300 ${
              sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          />

          <div
            className={`relative flex-1 flex flex-col max-w-xs w-full bg-gray-100 transform ease-in-out duration-300 ${
              sidebarOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4">
                <Bot className="h-8 w-8 text-gray-900" />
                <span className="ml-2 text-gray-900 font-medium text-xl">
                  Admin
                </span>
              </div>
              {renderNavigation()}
            </div>
          </div>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 bg-gray-100">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4">
              <Bot className="h-8 w-8 text-gray-900" />
              <span className="ml-2 text-gray-900 font-medium text-xl">
                Admin
              </span>
            </div>
            {renderNavigation()}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <Loader className="h-8 w-8 text-[#F98B3D] animate-spin" />
                </div>
              ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-red-700">{error}</p>
                      <button
                        onClick={() => window.location.reload()}
                        className="mt-2 text-sm text-red-600 hover:text-red-800"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <Outlet />
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
